/**
 * occurrence 겹침 레이아웃 (T-cal43) — 주/일 뷰의 동시간대 이벤트 side-by-side 배치.
 *
 * 캘린더 난제: 같은 시간대에 겹치는 이벤트들을 가로로 나눠(column) 배치해야 한다.
 * 표준 알고리즘(Google/Outlook 유사):
 *   1. 시작시각 오름차순 정렬(동률은 종료 늦은 것 먼저 — 더 긴 이벤트가 좌측).
 *   2. 겹침 그룹(cluster) 분리 — 직전까지의 최대 종료시각보다 시작이 빠르면 같은 그룹.
 *   3. 그룹 안에서 각 이벤트를 빈 column 에 그리디 배치(겹치지 않는 가장 왼쪽 column).
 *   4. 그룹의 column 수 = 너비 분모. 각 이벤트 left=col/cols, width=1/cols.
 *
 * 결정성: Date 시각만 사용(Math.random/Date.now 미사용). pure — 단위 테스트 가능.
 */

export interface LayoutInput {
  id: string; // 이벤트 식별 (occurrence 는 id+occurrenceAt 결합 권장)
  start: number; // epoch ms
  end: number; // epoch ms
}

export interface LayoutBox<T extends LayoutInput = LayoutInput> {
  item: T;
  /** 0..1 — 컨테이너 너비 대비 좌측 비율. */
  left: number;
  /** 0..1 — 컨테이너 너비 대비 폭. */
  width: number;
  /** 그룹 내 column index / 총 column 수 (디버그·접근성용). */
  column: number;
  columns: number;
}

/** 두 구간이 겹치는가 (경계 접촉은 비겹침 — end==start 는 연속). */
function overlaps(a: LayoutInput, b: LayoutInput): boolean {
  return a.start < b.end && b.start < a.end;
}

export function computeOverlapLayout<T extends LayoutInput>(items: T[]): Array<LayoutBox<T>> {
  if (items.length === 0) return [];
  // 1. 정렬 — 시작 asc, 동률은 종료 desc(긴 이벤트 좌측), 그래도 동률이면 id 안정 정렬.
  const sorted = [...items].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    if (a.end !== b.end) return b.end - a.end;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  const out: Array<LayoutBox<T>> = [];
  let cluster: T[] = [];
  let clusterMaxEnd = -Infinity;

  function flushCluster() {
    if (cluster.length === 0) return;
    // 3. 그리디 column 배치.
    const columnEnds: number[] = []; // 각 column 의 마지막 종료시각
    const assigned: Array<{ item: T; col: number }> = [];
    for (const it of cluster) {
      let col = columnEnds.findIndex((endMs) => endMs <= it.start);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(it.end);
      } else {
        columnEnds[col] = it.end;
      }
      assigned.push({ item: it, col });
    }
    const cols = columnEnds.length;
    for (const { item, col } of assigned) {
      out.push({ item, left: col / cols, width: 1 / cols, column: col, columns: cols });
    }
    cluster = [];
    clusterMaxEnd = -Infinity;
  }

  for (const it of sorted) {
    if (cluster.length > 0 && it.start >= clusterMaxEnd) {
      // 직전 그룹과 안 겹침 → 그룹 확정.
      flushCluster();
    }
    cluster.push(it);
    clusterMaxEnd = Math.max(clusterMaxEnd, it.end);
  }
  flushCluster();

  // 입력 순서와 무관하게 안정적 — 호출자가 id 로 매핑.
  return out;
}

export { overlaps as __overlaps };
